/**
 * IFC Link Service
 *
 * Real machinery behind "Link IFC" and "Link Coordination Model (.ifc)":
 * parses the file through the web-ifc wasm engine, bakes the whole model
 * into one IndexedDB asset (parts keep their authored colours) and hands
 * back the real extents. The link wrapper element references the asset id;
 * the scene renderer streams the mesh from the asset store — no schematic
 * stand-in box is ever created for a parsed file.
 */

import { parseIfcFile, IfcMeshPartRecord } from './IfcParserService';
import { storeIfcAsset } from './IfcAssetStore';

export interface IfcLinkPayload {
    /** IndexedDB asset holding every product mesh of the linked file. */
    assetId: string;
    productCount: number;
    schema: string;
    /** Axis-aligned extents in metres, re-centred around the model origin. */
    bbox: { min: [number, number, number]; max: [number, number, number] };
}

/**
 * Parse an .ifc file and park its geometry as one link asset. Throws with a
 * user-facing message when the engine cannot open the model — callers must
 * surface that instead of drawing a placeholder.
 */
export async function buildIfcLinkPayload(fileName: string, bytes: Uint8Array): Promise<IfcLinkPayload> {
    // World coordinates: a linked coordination model must land where the
    // file authored it, not re-centred per product.
    const parsed = await parseIfcFile(fileName, bytes, { recentre: false });
    if (parsed.products.length === 0) {
        throw new Error(`"${fileName}" contains no geometric products — nothing to link.`);
    }

    // One asset per file: parts carry per-vertex data already, colours ride
    // on each part, and the link renderer walks them as individual meshes.
    const parts: IfcMeshPartRecord[] = [];
    const min: [number, number, number] = [Infinity, Infinity, Infinity];
    const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
    for (const product of parsed.products) {
        for (const part of product.parts) {
            parts.push(part);
            for (let i = 0; i < part.positions.length; i += 3) {
                min[0] = Math.min(min[0], part.positions[i]);
                max[0] = Math.max(max[0], part.positions[i]);
                min[1] = Math.min(min[1], part.positions[i + 1]);
                max[1] = Math.max(max[1], part.positions[i + 1]);
                min[2] = Math.min(min[2], part.positions[i + 2]);
                max[2] = Math.max(max[2], part.positions[i + 2]);
            }
        }
    }
    if (parts.length === 0) {
        throw new Error(`"${fileName}" contains no renderable geometry.`);
    }

    // Re-centre like every other link: XY to the model extents centre, Z
    // down to the ground plane, so the linked model lands on the element
    // origin regardless of how far the shared site coordinates sit away.
    const cx = (min[0] + max[0]) / 2;
    const cy = (min[1] + max[1]) / 2;
    const cz = min[2];
    for (const part of parts) {
        for (let i = 0; i < part.positions.length; i += 3) {
            part.positions[i] -= cx;
            part.positions[i + 1] -= cy;
            part.positions[i + 2] -= cz;
        }
    }
    const centred = {
        min: [min[0] - cx, min[1] - cy, min[2] - cz] as [number, number, number],
        max: [max[0] - cx, max[1] - cy, max[2] - cz] as [number, number, number],
    };

    const assetId = await storeIfcAsset(fileName, fileName, parts, centred);
    return { assetId, productCount: parsed.products.length, schema: parsed.schema, bbox: centred };
}
